import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import { Home, Image as ImageIcon, ChevronDown, ChevronUp, Eye, Type, Plus, Trash2 } from 'lucide-react';
import ImagePickerModal from './ImagePickerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Canonical schema for the Home page. Each `type` matches what Home.jsx reads
// via getText(type, field) / getSection(type). Editing here writes correctly
// typed CMS sections so the public page picks them up.
const SCHEMA = [
  {
    type: 'hero', label: 'Hero (Top Banner)',
    fields: [
      { key: 'title', label: 'Headline', kind: 'text', def: 'The Best Place To Be In Mineral Wells, TX' },
      { key: 'image', label: 'Hero Image', kind: 'image', def: '/images/hero/hero-cone.png' },
    ],
  },
  {
    type: 'tagline', label: '"When Life Hands You Ice Cream" Heading',
    fields: [
      { key: 'title', label: 'Heading', kind: 'text', def: 'When Life Hands You Ice Cream, Eat It!' },
    ],
  },
  {
    type: 'polaroid', label: 'Polaroid Photo Collage',
    fields: [
      { key: 'main_image', label: 'Large Photo', kind: 'image', def: '/images/misc/polaroid-main.jpg' },
      { key: 'small_image_1', label: 'Top-Right Photo', kind: 'image', def: '/images/misc/polaroid-small1.jpg' },
      { key: 'small_image_2', label: 'Bottom-Right Photo', kind: 'image', def: '/images/misc/polaroid-small2.png' },
    ],
  },
  {
    type: 'taste_section', label: '"A Taste Worth Remembering" Section',
    fields: [
      { key: 'title', label: 'Heading', kind: 'text', def: 'A Taste Worth Remembering' },
      { key: 'left_image', label: 'Left Photo', kind: 'image', def: '/images/misc/brownie-stack.png' },
      { key: 'right_image', label: 'Right Photo', kind: 'image', def: '/images/misc/cookie-polaroid.png' },
    ],
  },
  {
    type: 'ice_cream_section', label: 'Ice Cream Selection Section',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', def: 'All Natural Ice Cream Selection' },
      { key: 'subtitle', label: 'Subtitle', kind: 'text', def: 'No Artificial Colors, Flavors, Preservatives or High Fructose Corn Syrup' },
    ],
  },
  {
    type: 'ice_cream_flavors', label: 'Featured Flavor Circles',
    items: {
      itemFields: [
        { key: 'name', label: 'Name', kind: 'text' },
        { key: 'image', label: 'Photo', kind: 'image' },
      ],
      def: [
        { name: 'Butter Pecan', image: '/images/icecream/butterpecan.webp' },
        { name: 'Chocolate Chip Cookie Dough', image: '/images/icecream/chocolatechipcookiedough.webp' },
        { name: 'Coffee Almond Fudge', image: '/images/icecream/coffeealmondfudge.webp' },
        { name: 'Cookies And Cream', image: '/images/icecream/cookiesandcream.webp' },
        { name: 'Mint Chocolate Chip', image: '/images/icecream/mintchocolatechip.webp' },
        { name: 'Mudslide', image: '/images/icecream/Mudslide.webp' },
      ],
    },
  },
  {
    type: 'specialties', label: 'Our Specialties',
    fields: [{ key: 'title', label: 'Title', kind: 'text', def: 'Our Specialties' }],
    items: {
      itemFields: [
        { key: 'name', label: 'Name', kind: 'text' },
        { key: 'image', label: 'Photo', kind: 'image' },
        { key: 'famous', label: 'Famous?', kind: 'bool' },
      ],
      def: [
        { name: 'Famous Ice Cream Stackers', image: '/images/specialties/stackers.png', famous: true },
        { name: 'Energy Drinks by Monin', image: '/images/specialties/energy-drinks.png', famous: false },
        { name: 'Cookies, Brownies, Tarts', image: '/images/specialties/cookies-brownies.png', famous: false },
        { name: 'Famous Ice Cream Nachos', image: '/images/specialties/nachos.png', famous: true },
        { name: 'Lattes, Espressos, Cappuccinos', image: '/images/specialties/coffee.png', famous: false },
        { name: 'Cakes, Pies', image: '/images/specialties/cakes.png', famous: false },
      ],
    },
  },
  {
    type: 'mural', label: 'Circus Mural Banner',
    fields: [{ key: 'image', label: 'Mural Image', kind: 'image', def: '/images/misc/circus-mural.png' }],
  },
  {
    type: 'features', label: '"Come See Us!" Section',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', def: 'Come See Us!' },
      { key: 'image', label: 'Staff / Storefront Photo', kind: 'image', def: '/images/misc/COMESEEUS.jpg' },
    ],
    items: {
      itemFields: [
        { key: 'title', label: 'Title', kind: 'text' },
        { key: 'description', label: 'Description', kind: 'textarea' },
        { key: 'icon', label: 'Icon path', kind: 'text' },
      ],
      def: [
        { icon: 'red-carpet', title: 'Beautiful Venue', description: 'Our 125 year-old building is located directly across from the historic Baker Hotel' },
        { icon: 'balloons', title: 'Fun & Family-friendly', description: 'Perfect for friends, date night, kids, family night-out & special occasions' },
        { icon: 'catering', title: 'Art Gallery & Music', description: "Famous local artists' works of paintings, photography, glass works & crafts. Most for sale" },
        { icon: 'wifi', title: 'Tasty Treats', description: 'Something for every taste and every budget. Choose from our specialties or build your own' },
      ],
    },
  },
  {
    type: 'pastries', label: 'Pastries Section',
    fields: [{ key: 'title', label: 'Title', kind: 'text', def: 'Pastries' }],
  },
  {
    type: 'drawing', label: 'Weekly Drawing Section',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', def: '🎉 Enter Our Weekly Drawing!' },
      { key: 'subtitle', label: 'Subtitle', kind: 'text', def: 'Win free ice cream & exclusive offers!' },
      { key: 'image', label: 'Side Image', kind: 'image', def: '/images/misc/1000202320.png' },
    ],
  },
];

const buildData = (sections) => {
  const d = {};
  SCHEMA.forEach((s) => {
    const sec = sections?.find((x) => x.type === s.type);
    const entry = {};
    (s.fields || []).forEach((f) => { entry[f.key] = sec?.[f.key] ?? f.def ?? ''; });
    if (s.items) {
      entry.items = (sec?.items && sec.items.length) ? sec.items.map((i) => ({ ...i })) : s.items.def.map((i) => ({ ...i }));
    }
    d[s.type] = entry;
  });
  return d;
};

const toSections = (data) => SCHEMA.map((s, i) => {
  const e = data[s.type] || {};
  const out = { id: s.type, type: s.type, order: i, is_active: true };
  (s.fields || []).forEach((f) => { out[f.key] = e[f.key] ?? ''; });
  if (s.items) out.items = e.items || [];
  return out;
});

const ImageField = ({ value, onPick }) => (
  <div className="flex items-center gap-3">
    <div className="w-20 h-20 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
      {value ? (
        <img src={value} alt="preview" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon className="w-6 h-6" /></div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 truncate mb-2">{value || 'No image selected'}</p>
      <Button type="button" variant="outline" size="sm" onClick={onPick} className="gap-1">
        <ImageIcon className="w-4 h-4" /> Change Image
      </Button>
    </div>
  </div>
);

const HomeEditor = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ page_title: '', meta_description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ hero: true });
  const [picker, setPicker] = useState(null); // { type, field } | { type, index, field }

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/page-content/admin/home`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const content = await res.json();
        setData(buildData(content?.sections || []));
        setMeta({
          page_title: content?.page_title || 'Home',
          meta_description: content?.meta_description || '',
        });
      } else {
        setData(buildData([]));
      }
    } catch (e) {
      console.error('Error loading home content', e);
      setData(buildData([]));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (type, field, value) => {
    setData((prev) => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const setItemField = (type, index, field, value) => {
    setData((prev) => {
      const items = [...(prev[type].items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, [type]: { ...prev[type], items } };
    });
  };

  const addItem = (type, itemFields) => {
    setData((prev) => {
      const blank = {};
      itemFields.forEach((f) => { blank[f.key] = f.kind === 'bool' ? false : ''; });
      return { ...prev, [type]: { ...prev[type], items: [...(prev[type].items || []), blank] } };
    });
  };

  const removeItem = (type, index) => {
    setData((prev) => {
      const items = [...(prev[type].items || [])];
      items.splice(index, 1);
      return { ...prev, [type]: { ...prev[type], items } };
    });
  };

  const handlePick = (url) => {
    if (!picker) return;
    if (picker.index !== undefined) {
      setItemField(picker.type, picker.index, picker.field, url);
    } else {
      setField(picker.type, picker.field, url);
    }
    setPicker(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/page-content/home`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_title: meta.page_title,
          meta_description: meta.meta_description,
          sections: toSections(data),
          is_published: true,
        }),
      });
      if (res.ok) {
        toast({ title: 'Saved', description: 'Home page updated. Refresh the site to see changes.' });
      } else {
        toast({ title: 'Error', description: 'Failed to save home page', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Save error', e);
      toast({ title: 'Error', description: 'Network error while saving', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggle = (type) => setExpanded((p) => ({ ...p, [type]: !p[type] }));

  if (loading || !data) return <div className="text-center py-8">Loading...</div>;

  const renderField = (type, f) => {
    if (f.kind === 'image') {
      return (
        <div key={f.key} data-testid={`home-field-${type}-${f.key}`}>
          <label className="block text-sm font-bold mb-2">{f.label}</label>
          <ImageField value={data[type][f.key]} onPick={() => setPicker({ type, field: f.key })} />
        </div>
      );
    }
    if (f.kind === 'textarea') {
      return (
        <div key={f.key}>
          <label className="block text-sm font-bold mb-2">{f.label}</label>
          <textarea
            data-testid={`home-field-${type}-${f.key}`}
            value={data[type][f.key] || ''}
            onChange={(e) => setField(type, f.key, e.target.value)}
            rows="2"
            className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
          />
        </div>
      );
    }
    return (
      <div key={f.key}>
        <label className="block text-sm font-bold mb-2">{f.label}</label>
        <input
          type="text"
          data-testid={`home-field-${type}-${f.key}`}
          value={data[type][f.key] || ''}
          onChange={(e) => setField(type, f.key, e.target.value)}
          className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
        />
      </div>
    );
  };

  return (
    <div data-testid="home-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black flex items-center gap-2">
            <Home className="w-7 h-7 text-pink-500" /> Home Page Editor
          </h2>
          <p className="text-gray-600 mt-1 text-sm">Edit all homepage text and photos. Changes go live after saving.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button onClick={() => window.open('/', '_blank')} variant="outline" className="gap-2">
            <Eye className="w-4 h-4" /> Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-pink-500 hover:bg-pink-600 gap-2 px-8" data-testid="home-save-button">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* SEO / Page meta */}
      <div className="bg-white rounded-xl border-2 border-pink-200 p-4 md:p-6 mb-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Type className="w-5 h-5 text-pink-500" /> Page & SEO</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold mb-2">Page Title (SEO)</label>
            <input
              type="text"
              data-testid="home-page-title"
              value={meta.page_title}
              onChange={(e) => setMeta({ ...meta, page_title: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-2">Meta Description (SEO)</label>
            <input
              type="text"
              data-testid="home-meta-description"
              value={meta.meta_description}
              onChange={(e) => setMeta({ ...meta, meta_description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SCHEMA.map((s) => (
          <div key={s.type} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(s.type)}
              className="w-full p-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
              data-testid={`home-section-toggle-${s.type}`}
            >
              <span className="font-bold text-left">{s.label}</span>
              {expanded[s.type] ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>

            {expanded[s.type] && (
              <div className="p-4 border-t space-y-4">
                {(s.fields || []).map((f) => renderField(s.type, f))}

                {/* Repeatable items (flavors, specialties, features) */}
                {s.items && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <label className="block text-sm font-bold">Items ({data[s.type].items?.length || 0})</label>
                      <Button type="button" size="sm" onClick={() => addItem(s.type, s.items.itemFields)} className="bg-pink-500 hover:bg-pink-600 gap-1">
                        <Plus className="w-4 h-4" /> Add Item
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {data[s.type].items?.map((item, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg" data-testid={`home-item-${s.type}-${idx}`}>
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-bold text-gray-500">Item #{idx + 1}</span>
                            <button type="button" onClick={() => removeItem(s.type, idx)} className="text-red-500 hover:text-red-700 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {s.items.itemFields.map((f) => {
                              if (f.kind === 'image') {
                                return (
                                  <div key={f.key} className="sm:col-span-2">
                                    <label className="block text-xs font-bold mb-1">{f.label}</label>
                                    <ImageField value={item[f.key]} onPick={() => setPicker({ type: s.type, index: idx, field: f.key })} />
                                  </div>
                                );
                              }
                              if (f.kind === 'bool') {
                                return (
                                  <label key={f.key} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!item[f.key]}
                                      onChange={(e) => setItemField(s.type, idx, f.key, e.target.checked)}
                                      className="w-5 h-5 accent-pink-500"
                                    />
                                    <span className="text-sm font-bold">{f.label}</span>
                                  </label>
                                );
                              }
                              if (f.kind === 'textarea') {
                                return (
                                  <div key={f.key} className="sm:col-span-2">
                                    <label className="block text-xs font-bold mb-1">{f.label}</label>
                                    <textarea
                                      value={item[f.key] || ''}
                                      onChange={(e) => setItemField(s.type, idx, f.key, e.target.value)}
                                      rows="2"
                                      className="w-full px-3 py-1 rounded border focus:border-pink-500 focus:outline-none text-sm"
                                    />
                                  </div>
                                );
                              }
                              return (
                                <div key={f.key}>
                                  <label className="block text-xs font-bold mb-1">{f.label}</label>
                                  <input
                                    type="text"
                                    value={item[f.key] || ''}
                                    onChange={(e) => setItemField(s.type, idx, f.key, e.target.value)}
                                    className="w-full px-3 py-1 rounded border focus:border-pink-500 focus:outline-none text-sm"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Save (bottom) */}
      <div className="flex justify-center pt-6 mt-6 border-t">
        <Button onClick={handleSave} disabled={saving} className="bg-pink-500 hover:bg-pink-600 gap-2 px-10">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <ImagePickerModal
        open={!!picker}
        onClose={() => setPicker(null)}
        onSelect={handlePick}
        currentImage={
          picker
            ? (picker.index !== undefined
              ? data[picker.type]?.items?.[picker.index]?.[picker.field]
              : data[picker.type]?.[picker.field]) || ''
            : ''
        }
      />
    </div>
  );
};

export default HomeEditor;
